/*@flow*/
import React, { useReducer, useEffect, forwardRef } from 'react';
import * as t from 'lib/t';
import { ValidationResult } from 'lib/t/validate';
import factory from './components/factory';
import type { PropsT, PathT } from './types';
import hasOwn from 'lib/hasOwn';
import Blank from 'lib/Blank';
import getTypeFromUnion from './util/getTypeFromUnion';
import commands from './commands';
import mkState from './mkState';
import reducer from './reducer';
import FormContext from './context';

class FormContainer extends React.Component<*> {
  static contextType = FormContext;

  validate(): t.ValidationResultT {
    const ctx = this.context;
    const { value, type } = ctx.state;
    let result = ValidationResult({ errors: [], value });
    try {
      result = t.validate(value, type, {});
      // Store the errors in state.
      commands(ctx).set('errors', result.errors);
    } catch (_) {} //eslint-disable-line
    return result;
  }

  getValue() {
    const result = this.validate();
    if (this.props.isControlled) {
      console.error(
        '[proforma] This is a controlled component. You cannot use .getValue().'
      );
      return null;
    }
    return result && result.isValid() ? result.value : null;
  }

  render() {
    const ctx = this.context;
    const { value, type } = ctx.state;
    if (Blank.is(type)) return null;
    const concreteType = getTypeFromUnion(type, value);
    const options2 = concreteType.meta.options || {};
    const SubComponent = factory(concreteType, options2);
    const path: PathT = [];
    return <SubComponent path={path} />;
  }
}

export default (forwardRef<PropsT<*>, FormContainer>(
  (props: PropsT<*>, myRef: React$Ref<*>) => {
    const [state, dispatch] = useReducer(reducer, mkState(props));
    const ctx = { state, dispatch };
    const isControlled = hasOwn.call(props, 'value');

    useEffect(() => {
      commands(ctx).set('type', () => props.type);
    }, [props.type]);

    useEffect(() => {
      commands(ctx).set('onChange', () => props.onChange);
    }, [props.onChange]);

    useEffect(() => {
      if (!hasOwn.call(props, 'defaultValue')) return;
      commands(ctx).set('value', props.defaultValue);

      return () => {
        state.uidGenerator.reset();
      };
    }, []);

    useEffect(() => {
      if (!props.onChange) return;
      if (!props.value) return;
      if (!isControlled) {
        console.error(
          '[proforma] This is not a controlled components and cannot be updated via `value` prop.'
        );
        return;
      }
      commands(ctx).set('value', props.value);
    }, [props.value]);

    if (hasOwn.call(props, 'options')) {
      console.error('Form cannot have an options field. Please refactor.');
      console.error(props);
    }

    return (
      <FormContext.Provider value={ctx}>
        <FormContainer ref={myRef} isControlled={isControlled} />
      </FormContext.Provider>
    );
  }
): React$AbstractComponent<PropsT<>, FormContainer>);
